import React from 'react';

import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowList from './components/WorkflowList';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [{ label: '事前準備', title: '環境構築', active: true }];

const HandsonPreparation: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="事前準備編 — 環境構築"
      seriesName="事前準備編"
      tags={['Hands-on', 'Setup', 'Environment']}
      seriesSteps={seriesSteps}
      nextLink={{
        label: 'ストーリー導入 + プロジェクト雛形作成',
        path: '/devdocs/handson/story-scaffold',
      }}
    >
      <WorkflowGoalBox
        prerequisites={['なし（このシリーズの最初の記事です）']}
        completionCriteria={[
          'Kiroがインストールされ、正常に起動している',
          'Java 21がインストールされ、java -version でバージョンが確認できる',
          'Docker Engineがインストールされ、docker --version と docker compose version が実行できる',
          'Gitがインストールされ、git --version でバージョンが確認できる',
          'GitHubアカウントが作成され、SSHまたはHTTPSでの接続が確認できている',
          'Node.js（v18以上）がインストールされ、node -v と npm -v でバージョンが確認できる',
          'curlがインストールされ、curl --version でバージョンが確認できる',
        ]}
      >
        ハンズオンを進めるために必要なツール・環境を全てインストールし、正常に動作することを確認する。
      </WorkflowGoalBox>

      {/* 目次 */}
      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'はじめに' },
          { id: 'section-2', label: '必要なツール一覧' },
          { id: 'section-3', label: 'Kiroのインストール' },
          { id: 'section-4', label: 'Java 21のインストール' },
          { id: 'section-5', label: 'Docker Engineのインストール' },
          { id: 'section-6', label: 'Gitのインストール' },
          { id: 'section-7', label: 'GitHubアカウントの作成と接続設定' },
          { id: 'section-8', label: 'Node.jsのインストール' },
          { id: 'section-9', label: 'curlの確認' },
          { id: 'section-10', label: '環境確認チェックリスト' },
          { id: 'section-11', label: 'ライブラリ・ツールのライセンス一覧' },
        ]}
      />

      {/* セクション1: はじめに */}
      <WorkflowSection title="はじめに" id="section-1" number={1}>
        <WorkflowText>
          AI駆動開発はまだ発展途上の分野です。このハンズオンで紹介するアプローチは現時点での実践知見に基づいており、ツールの進化や知見の蓄積に伴い変わる可能性があります。
        </WorkflowText>

        <WorkflowText>
          このハンズオンではバックエンド（Java / Spring Boot）とフロントエンド（React /
          TypeScript）の両方を扱います。全ての記事をスムーズに進められるよう、必要な環境を事前にまとめてセットアップします。
        </WorkflowText>

        <WorkflowInfoBox type="info">
          <Typography variant="body2" fontWeight={600}>
            ポイント
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            すでにインストール済みのツールはスキップしてOKです。各セクションの「動作確認」コマンドを実行して、正しいバージョンが表示されればそのセクションは飛ばして構いません。最後の「環境確認チェックリスト」で全項目パスすればこの記事は完了です。
          </Typography>
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* セクション2: 必要なツール一覧 */}
      <WorkflowSection title="必要なツール一覧" id="section-2" number={2}>
        <WorkflowTable
          headers={['ツール', 'バージョン', '用途', '使用開始']}
          rows={[
            ['Kiro', '最新', 'AI駆動開発IDE', '導入編〜'],
            ['Java (JDK)', '21', 'バックエンド開発', '導入編〜'],
            ['Docker Engine', '最新', 'MySQL・Flyway等のコンテナ実行', '導入編〜'],
            ['Git', '最新', 'バージョン管理', '導入編〜'],
            ['GitHubアカウント', '—', 'リポジトリホスティング', '導入編〜'],
            ['Node.js', '18以上', 'フロントエンド開発', 'フロントエンド編〜'],
            ['curl', '最新', 'APIの動作確認', '導入編〜'],
          ]}
        />

        <WorkflowInfoBox type="warning">
          <Typography variant="body2">
            <strong>注意</strong>: Docker <strong>Desktop</strong>{' '}
            は使用しません（ライセンスの関係）。Docker <strong>Engine</strong>{' '}
            をインストールしてください。macOSの場合はColima等のランタイムを利用します。
          </Typography>
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* セクション3: Kiroのインストール */}
      <WorkflowSection title="Kiroのインストール" id="section-3" number={3}>
        <WorkflowText>
          KiroはAI駆動開発のためのIDEです。VS Codeベースで、AIとの対話機能が統合されています。
        </WorkflowText>

        <WorkflowSection title="ダウンロードとインストール" subNumber="3.1">
          <WorkflowList
            variant="ordered"
            items={[
              <Typography key="1" variant="body2" color="text.secondary">
                <Link href="https://kiro.dev/" target="_blank" rel="noopener">
                  Kiro公式サイト
                </Link>{' '}
                にアクセスする
              </Typography>,
              'お使いのOSに合ったインストーラーをダウンロードする',
              'インストーラーの指示に従ってインストールする',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="初期設定" subNumber="3.2">
          <WorkflowList
            variant="ordered"
            items={[
              'Kiroを起動する',
              'アカウントのサインイン（またはサインアップ）を完了する',
              '初回セットアップのウィザードが表示された場合は指示に従う',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="動作確認" subNumber="3.3">
          <WorkflowText>
            Kiroが正常に起動し、チャットパネルが表示されることを確認してください。チャットに何か入力して応答が返ってくればOKです。
          </WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: Java 21のインストール */}
      <WorkflowSection title="Java 21のインストール" id="section-4" number={4}>
        <WorkflowSection title="macOS" subNumber="4.1">
          <WorkflowText>Homebrew を使用する場合:</WorkflowText>
          <WorkflowCodeBlock language="bash" code="brew install openjdk@21" />
          <WorkflowText>インストール後、シンボリックリンクを作成します。</WorkflowText>
          <WorkflowCodeBlock
            language="bash"
            code="sudo ln -sfn $(brew --prefix openjdk@21)/libexec/openjdk.jdk /Library/Java/JavaVirtualMachines/openjdk-21.jdk"
          />
          <WorkflowText>SDKMAN! を使用する場合:</WorkflowText>
          <WorkflowCodeBlock language="bash" code="sdk install java 21-open" />
        </WorkflowSection>

        <WorkflowSection title="Windows" subNumber="4.2">
          <WorkflowText>winget を使用する場合:</WorkflowText>
          <WorkflowCodeBlock language="powershell" code="winget install Microsoft.OpenJDK.21" />
          <WorkflowText>手動インストールの場合:</WorkflowText>
          <WorkflowList
            variant="ordered"
            items={[
              <Typography key="1" variant="body2" color="text.secondary">
                <Link href="https://adoptium.net/" target="_blank" rel="noopener">
                  Adoptium (Eclipse Temurin)
                </Link>{' '}
                にアクセスする
              </Typography>,
              'JDK 21 の Windows用インストーラー (.msi) をダウンロードする',
              'インストーラーを実行する（「Set JAVA_HOME variable」にチェックを入れる）',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="動作確認（共通）" subNumber="4.3">
          <WorkflowCodeBlock language="bash" code="java -version" />
          <WorkflowText>{`openjdk version "21.x.x"`} のように表示されればOKです。</WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5: Docker Engineのインストール */}
      <WorkflowSection title="Docker Engineのインストール" id="section-5" number={5}>
        <WorkflowInfoBox type="warning">
          <Typography variant="body2">
            <strong>注意</strong>: Docker <strong>Desktop</strong> ではなく Docker{' '}
            <strong>Engine</strong> を使用します。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowTipBox title="なぜDocker Desktopを使わないのか？">
          <WorkflowText>
            Docker
            Desktopは2021年8月のライセンス変更により、従業員250名以上または年間売上1,000万ドル（約15億円）以上の企業での利用には有料サブスクリプションが必要になりました。
          </WorkflowText>
          <WorkflowText>
            Arditoは年間売上が15億円を超えているため、Docker
            Desktopを業務利用する場合は有料ライセンスの契約が必要です。このハンズオンはArdito社員向けの研修であるため、ライセンス費用が発生しないDocker
            Engineを使用します。
          </WorkflowText>
          <WorkflowText>
            Docker Engine自体は無料です。Docker
            DesktopはGUIやVM管理を含む「デスクトップアプリケーション」であり、その部分にライセンスがかかっています。Docker
            EngineのコアはApache License 2.0のオープンソースであり、商用利用に制限はありません。
          </WorkflowText>
          <WorkflowText>
            個人学習や小規模企業（上記条件に該当しない）であればDocker Desktopを無料で利用できます。
          </WorkflowText>
        </WorkflowTipBox>

        <WorkflowSection title="macOS" subNumber="5.1">
          <WorkflowText>
            macOSでDocker Engineを利用するにはLinux VM上で動かす必要があります。Colima
            が軽量でおすすめです。
          </WorkflowText>
          <WorkflowCodeBlock language="bash" code="brew install docker docker-compose colima" />
          <WorkflowText>Colimaを起動します。</WorkflowText>
          <WorkflowCodeBlock language="bash" code="colima start" />

          <WorkflowTipBox title="Colimaとは？">
            <WorkflowText>
              ColimaはmacOS/Linux上でDockerコンテナを実行するための軽量ランタイムです。Docker
              Desktopの代替として使えます。内部的にはLima（Linux VM）上でDocker
              Engineを動かしています。
            </WorkflowText>
            <WorkflowText>
              {`colima start`} でVMが起動し、以降は通常通り docker
              コマンドが使えるようになります。PCを再起動した場合は再度 colima start が必要です。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="Windows" subNumber="5.2">
          <WorkflowText>Windows では WSL2 + Docker Engine の構成を使用します。</WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            ステップ1: WSL2を有効化する
          </Typography>
          <WorkflowText>PowerShell（管理者として実行）で以下を実行します。</WorkflowText>
          <WorkflowCodeBlock language="powershell" code="wsl --install" />
          <WorkflowText>再起動を求められた場合は再起動してください。</WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            ステップ2: WSL2上にDockerをインストールする
          </Typography>
          <WorkflowText>WSL2のターミナル（Ubuntu）を開いて以下を実行します。</WorkflowText>
          <WorkflowCodeBlock
            language="bash"
            code={`# Docker公式のインストールスクリプト
curl -fsSL https://get.docker.com | sh

# 現在のユーザーをdockerグループに追加（sudo無しで実行可能にする）
sudo usermod -aG docker $USER`}
          />
          <WorkflowText>
            グループの変更を反映するため、WSLターミナルを一度閉じて再度開きます。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            ステップ3: Docker Composeをインストールする
          </Typography>
          <WorkflowCodeBlock
            language="bash"
            code={`sudo apt-get update
sudo apt-get install docker-compose-plugin`}
          />

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            ステップ4: Dockerサービスを起動する
          </Typography>
          <WorkflowCodeBlock language="bash" code="sudo service docker start" />

          <WorkflowTipBox title="WSL2の自動起動設定">
            <WorkflowText>
              Windowsを起動するたびに sudo service docker start
              を実行するのは手間です。以下のいずれかの方法で自動化できます。
            </WorkflowText>
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
              方法1: .bashrcに追記する
            </Typography>
            <WorkflowCodeBlock
              language="bash"
              code="echo 'sudo service docker start' >> ~/.bashrc"
            />
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
              方法2: systemdを有効化する（WSL2 0.67.6以降）
            </Typography>
            <WorkflowText>/etc/wsl.conf に以下を追加します。</WorkflowText>
            <WorkflowCodeBlock
              language="ini"
              code={`[boot]
systemd=true`}
            />
            <WorkflowText>WSLを再起動すれば、Docker Engineが自動で起動します。</WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="動作確認（共通）" subNumber="5.3">
          <WorkflowCodeBlock
            language="bash"
            code={`docker --version
docker compose version`}
          />
          <WorkflowText>
            それぞれバージョンが表示されればOKです。コンテナの起動も確認しましょう。
          </WorkflowText>
          <WorkflowCodeBlock language="bash" code="docker run --rm hello-world" />
          <WorkflowText>
            「Hello from Docker!」というメッセージが表示されれば正常に動作しています。
          </WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6: Gitのインストール */}
      <WorkflowSection title="Gitのインストール" id="section-6" number={6}>
        <WorkflowSection title="macOS" subNumber="6.1">
          <WorkflowText>
            macOSにはXcode Command Line
            Toolsに含まれるGitがプリインストールされていることが多いです。
          </WorkflowText>
          <WorkflowCodeBlock language="bash" code="git --version" />
          <WorkflowText>バージョンが表示されない場合はインストールします。</WorkflowText>
          <WorkflowCodeBlock
            language="bash"
            code={`# Xcode Command Line Tools 経由
xcode-select --install

# または Homebrew 経由
brew install git`}
          />
        </WorkflowSection>

        <WorkflowSection title="Windows" subNumber="6.2">
          <WorkflowText>winget を使用する場合:</WorkflowText>
          <WorkflowCodeBlock language="powershell" code="winget install Git.Git" />
          <WorkflowText>手動インストールの場合:</WorkflowText>
          <WorkflowList
            variant="ordered"
            items={[
              <Typography key="1" variant="body2" color="text.secondary">
                <Link href="https://gitforwindows.org/" target="_blank" rel="noopener">
                  Git for Windows
                </Link>{' '}
                にアクセスする
              </Typography>,
              'インストーラーをダウンロードして実行する',
              'デフォルト設定のままインストールする（Git Bashも一緒にインストールされる）',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="初期設定（共通）" subNumber="6.3">
          <WorkflowText>
            初めてGitを使う場合は、ユーザー名とメールアドレスを設定します。
          </WorkflowText>
          <WorkflowCodeBlock
            language="bash"
            code={`git config --global user.name "あなたの名前"
git config --global user.email "your-email@example.com"`}
          />
        </WorkflowSection>

        <WorkflowSection title="動作確認（共通）" subNumber="6.4">
          <WorkflowCodeBlock language="bash" code="git --version" />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション7: GitHubアカウントの作成と接続設定 */}
      <WorkflowSection title="GitHubアカウントの作成と接続設定" id="section-7" number={7}>
        <WorkflowSection title="アカウント作成" subNumber="7.1">
          <WorkflowText>
            GitHubアカウントをまだ持っていない場合は{' '}
            <Link href="https://github.com/" target="_blank" rel="noopener">
              GitHub
            </Link>{' '}
            でサインアップしてください。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="SSH接続の設定（推奨）" subNumber="7.2">
          <WorkflowText>
            SSH接続を設定しておくと、push/pullのたびにパスワードを入力する必要がなくなります。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            SSHキーの生成:
          </Typography>
          <WorkflowCodeBlock
            language="bash"
            code={`ssh-keygen -t ed25519 -C "your-email@example.com"`}
          />
          <WorkflowText>
            Enterキーを押してデフォルトの保存先と空のパスフレーズ（または任意のパスフレーズ）で進めます。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            GitHubに公開鍵を登録する:
          </Typography>

          <WorkflowText>公開鍵をクリップボードにコピーする:</WorkflowText>
          <WorkflowCodeBlock title="macOS" language="bash" code="pbcopy < ~/.ssh/id_ed25519.pub" />
          <WorkflowCodeBlock
            title="Windows（Git Bash）"
            language="bash"
            code="cat ~/.ssh/id_ed25519.pub | clip"
          />

          <WorkflowList
            variant="ordered"
            items={[
              <Typography key="1" variant="body2" color="text.secondary">
                <Link href="https://github.com/settings/keys" target="_blank" rel="noopener">
                  GitHub {'>'} Settings {'>'} SSH and GPG keys
                </Link>{' '}
                にアクセスする
              </Typography>,
              '「New SSH key」をクリックする',
              'Title に任意の名前（例: My Laptop）を入力する',
              'Key欄にコピーした公開鍵を貼り付ける',
              '「Add SSH key」をクリックする',
            ]}
          />

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            接続確認:
          </Typography>
          <WorkflowCodeBlock language="bash" code="ssh -T git@github.com" />
          <WorkflowText>
            {`Hi {ユーザー名}! You've successfully authenticated`} と表示されればOKです。
          </WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション8: Node.jsのインストール */}
      <WorkflowSection title="Node.jsのインストール" id="section-8" number={8}>
        <WorkflowText>
          フロントエンド編（記事5-1〜5-4）で使用します。バックエンド編だけ先に進めたい場合は後からインストールしても構いません。
        </WorkflowText>

        <WorkflowSection title="macOS" subNumber="8.1">
          <WorkflowText>Homebrew を使用する場合:</WorkflowText>
          <WorkflowCodeBlock language="bash" code="brew install node@22" />
          <WorkflowText>nvm を使用する場合（バージョン管理がしやすい）:</WorkflowText>
          <WorkflowCodeBlock
            language="bash"
            code={`# nvmのインストール
curl -o- https://raw.githubusercontent.com/nvm-sh/nvm/v0.40.1/install.sh | bash

# ターミナルを再起動してから
nvm install 22
nvm use 22`}
          />
        </WorkflowSection>

        <WorkflowSection title="Windows" subNumber="8.2">
          <WorkflowText>winget を使用する場合:</WorkflowText>
          <WorkflowCodeBlock language="powershell" code="winget install OpenJS.NodeJS.LTS" />
          <WorkflowText>nvm-windows を使用する場合:</WorkflowText>
          <WorkflowList
            variant="ordered"
            items={[
              <Typography key="1" variant="body2" color="text.secondary">
                <Link
                  href="https://github.com/coreybutler/nvm-windows/releases"
                  target="_blank"
                  rel="noopener"
                >
                  nvm-windows
                </Link>{' '}
                から最新のインストーラーをダウンロードする
              </Typography>,
              'インストーラーを実行する',
              'PowerShell またはコマンドプロンプトで以下を実行する',
            ]}
          />
          <WorkflowCodeBlock
            language="powershell"
            code={`nvm install 22
nvm use 22`}
          />
        </WorkflowSection>

        <WorkflowSection title="動作確認（共通）" subNumber="8.3">
          <WorkflowCodeBlock
            language="bash"
            code={`node -v
npm -v`}
          />
          <WorkflowText>Node.jsが v18.x.x 以上、npmのバージョンが表示されればOKです。</WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション9: curlの確認 */}
      <WorkflowSection title="curlの確認" id="section-9" number={9}>
        <WorkflowText>
          curlはAPIの動作確認で使用します。ほとんどのOSに標準でインストールされています。
        </WorkflowText>

        <WorkflowSection title="macOS" subNumber="9.1">
          <WorkflowText>macOSにはプリインストールされています。確認のみ行います。</WorkflowText>
          <WorkflowCodeBlock language="bash" code="curl --version" />
        </WorkflowSection>

        <WorkflowSection title="Windows" subNumber="9.2">
          <WorkflowText>
            Windows
            10以降にはプリインストールされています。PowerShellまたはコマンドプロンプトで確認します。
          </WorkflowText>
          <WorkflowCodeBlock language="powershell" code="curl --version" />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意（Windows）</strong>: PowerShellでは curl は Invoke-WebRequest
              のエイリアスになっている場合があります。ハンズオンで使用するcurlコマンドは{' '}
              <strong>Git Bash</strong> または <strong>WSL2のターミナル</strong>{' '}
              で実行してください。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowText>バージョンが表示されない場合（古いWindowsの場合）:</WorkflowText>
          <WorkflowCodeBlock language="powershell" code="winget install cURL.cURL" />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション10: 環境確認チェックリスト */}
      <WorkflowSection title="環境確認チェックリスト" id="section-10" number={10}>
        <WorkflowText>
          全てのインストールが完了したら、以下のコマンドを順番に実行して全てパスすることを確認してください。
        </WorkflowText>

        <WorkflowTable
          headers={['#', '確認コマンド', '期待結果', 'チェック']}
          rows={[
            ['1', 'java -version', 'openjdk version "21.x.x"', '☐'],
            ['2', 'docker --version', 'Docker version 2x.x.x 以上', '☐'],
            ['3', 'docker compose version', 'Docker Compose version v2.x.x', '☐'],
            ['4', 'docker run --rm hello-world', 'Hello from Docker! が表示される', '☐'],
            ['5', 'git --version', 'git version 2.x.x', '☐'],
            ['6', 'ssh -T git@github.com', 'Hi {ユーザー名}! ... authenticated', '☐'],
            ['7', 'node -v', 'v18.x.x 以上', '☐'],
            ['8', 'npm -v', 'バージョンが表示される', '☐'],
            ['9', 'curl --version', 'バージョンが表示される', '☐'],
            ['10', 'Kiro起動', 'チャットパネルが表示され、AIが応答する', '☐'],
          ]}
        />

        <WorkflowInfoBox type="success">
          <Typography variant="body2" fontWeight={600}>
            全てチェックがついたら事前準備完了です！
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            次の記事「導入編 — ストーリー導入 + プロジェクト雛形作成」に進みましょう。
          </Typography>
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* セクション11: ライセンス一覧 */}
      <WorkflowSection title="ライブラリ・ツールのライセンス一覧" id="section-11" number={11}>
        <WorkflowTipBox title="なぜライセンスを気にする必要があるのか？">
          <WorkflowText>
            オープンソースソフトウェア（OSS）は「無料で使える」と思われがちですが、実際には各ライセンスごとに利用条件が定められています。条件に違反すると法的リスクが発生する場合があります。
          </WorkflowText>
          <WorkflowText>
            特に商用プロダクトで利用する場合、コピーレフト型ライセンス（GPL等）は派生物にも同じライセンスの適用が求められる場合があること、OSSのライセンスは後から変更されることがあること、デュアルライセンスで商用利用時は有料ライセンスが必要になる製品もあることに注意が必要です。
          </WorkflowText>
          <WorkflowText>
            年次の低いうちやSESでは日常的にライセンスを意識する場面は少ないかもしれませんが、「自分が使っているツールのライセンスを把握している」ことはプロの開発者として重要な素養です。新しいライブラリを導入する際は、ライセンスを確認する習慣をつけましょう。
          </WorkflowText>
        </WorkflowTipBox>

        <WorkflowSection title="代表的なライセンスの種類" subNumber="11.1">
          <WorkflowText>
            OSSライセンスは数十種類以上存在しますが、ここではハンズオンで登場するものを中心に代表的なものを紹介します。実務で見慣れないライセンスに遭遇した場合は、必ず原文を確認してください。
          </WorkflowText>
          <WorkflowTable
            headers={['ライセンス種類', '特徴', '商用利用']}
            rows={[
              [
                'Apache License 2.0',
                '非常に寛容。改変・再配布自由。特許権の付与あり。著作権表示とライセンス文の同梱が必要',
                '自由',
              ],
              ['MIT License', '最も寛容。改変・再配布自由。著作権表示のみ必要', '自由'],
              ['BSD License', 'MITとほぼ同等。著作権表示が必要', '自由'],
              [
                'GPL v2 / v3',
                'コピーレフト型。派生物にも同じライセンスの適用が必要。ソースコード公開義務あり',
                '条件付き',
              ],
              ['LGPL', 'GPLの緩和版。ライブラリとしてリンクする場合はソース公開不要', '通常は自由'],
              [
                '独自ライセンス（商用）',
                'ベンダーが個別に定める条件。無料枠と有料枠がある場合が多い',
                '要確認',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="開発ツール" subNumber="11.2">
          <WorkflowTable
            headers={['ツール', 'ライセンス', '注意点']}
            rows={[
              ['Kiro', '独自（AWS）', '無料プランあり。利用規約に従う'],
              ['Docker Engine', 'Apache License 2.0', '商用利用自由。Docker Desktopは別ライセンス'],
              ['Docker Desktop', '独自（Docker社）', '大企業は有料。このハンズオンでは使用しない'],
              ['Git', 'GPL v2', 'ツールとして使用する分には問題なし'],
              ['Gradle', 'Apache License 2.0', '商用利用自由'],
              ['Colima', 'MIT License', '商用利用自由'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="バックエンド（Java / Spring Boot）" subNumber="11.3">
          <WorkflowTable
            headers={['ライブラリ', 'ライセンス', '注意点']}
            rows={[
              [
                'OpenJDK 21',
                'GPL v2 + Classpath Exception',
                'Classpath Exceptionにより商用利用自由',
              ],
              ['Spring Boot', 'Apache License 2.0', '商用利用自由'],
              ['Spring Framework', 'Apache License 2.0', '商用利用自由'],
              ['Spring Security', 'Apache License 2.0', '商用利用自由'],
              ['MyBatis', 'Apache License 2.0', '商用利用自由'],
              ['MySQL (Community)', 'GPL v2', 'サーバーとしての利用は問題なし'],
              [
                'MySQL Connector/J',
                'GPL v2 + FOSS Exception',
                'OSSで使用する場合はFOSS Exceptionが適用',
              ],
              ['Flyway (Community)', 'Apache License 2.0', '商用利用自由'],
              ['Lombok', 'MIT License', '商用利用自由'],
              ['Hibernate Validator', 'Apache License 2.0', '商用利用自由'],
              ['Apache Commons Lang3', 'Apache License 2.0', '商用利用自由'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="フロントエンド（React / TypeScript）" subNumber="11.4">
          <WorkflowTable
            headers={['ライブラリ', 'ライセンス', '注意点']}
            rows={[
              ['Node.js', 'MIT License', '商用利用自由'],
              ['React', 'MIT License', '商用利用自由'],
              ['TypeScript', 'Apache License 2.0', '商用利用自由'],
              ['Vite', 'MIT License', '商用利用自由'],
              ['MUI (Material UI)', 'MIT License', '商用利用自由。有料版は別ライセンス'],
              ['React Router', 'MIT License', '商用利用自由'],
              ['TanStack Query', 'MIT License', '商用利用自由'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="ライセンスに関する注意事項" subNumber="11.5">
          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>ライセンスは変更されることがあります。</strong>{' '}
              上記の情報は執筆時点（2026年8月）のものです。OSSプロジェクトはバージョンアップに伴いライセンスを変更する場合があります。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowText>過去に大きな影響を与えたライセンス変更の例:</WorkflowText>
          <WorkflowTable
            headers={['プロダクト', '変更内容', '時期']}
            rows={[
              ['Docker Desktop', '大企業での無料利用を制限', '2021年'],
              ['Elasticsearch', 'Apache 2.0 → SSPL + Elastic License', '2021年'],
              ['Redis', 'BSD → RSAL + SSPL（一部モジュール）', '2024年'],
              ['HashiCorp (Terraform等)', 'MPL 2.0 → BSL 1.1', '2023年'],
            ]}
          />

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              新しいライブラリやツールを導入する際は、必ずライセンスを確認する習慣をつけましょう。特にプロダクション環境で使用するもの、クライアントに納品するものについては、自社の法務部門やリーダーに確認を取ることが重要です。「知らなかった」は通用しません。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* 次のステップ */}
      <WorkflowInfoBox type="preview">
        <Typography variant="body2" fontWeight={600}>
          次の記事: 導入編 — ストーリー導入 + プロジェクト雛形作成
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          友人の相談からプロジェクトが始まる
        </Typography>
      </WorkflowInfoBox>
    </HandsonArticleLayout>
  );
};

export default HandsonPreparation;
